import { useRef } from 'react';
import {
  ArrowsLeftRightIcon,
  BracketsCurlyIcon,
  PulseIcon,
  ShieldCheckIcon,
  type Icon,
} from '@phosphor-icons/react';
import { DashboardMock, playDashboard } from '@/components/dashboard-mock';
import { Reveal } from '@/components/reveal';
import { Section, shellClass } from '@/components/section';
import { SectionHeader, playHeader } from '@/components/section-header';
import { copy, type GatewayFeatureId } from '@/content/copy';
import { cn } from '@/lib/cn';
import { useRevealOnEnter } from '@/scroll/use-reveal-on-enter';

const { gateway } = copy;

const ICONS: Record<GatewayFeatureId, Icon> = {
  orchestration: ArrowsLeftRightIcon,
  dashboard: PulseIcon,
  controls: ShieldCheckIcon,
  api: BracketsCurlyIcon,
};

/**
 * What the gateway does, beside a preview of what running it looks like. The feature list
 * reveals with the header; the dashboard mock plays its own count-up and chart draw.
 */
export function Gateway() {
  const ref = useRef<HTMLElement>(null);

  useRevealOnEnter(ref, (tl, q) => {
    const at = playHeader(tl, q);
    tl.to(q('[data-feature]'), { opacity: 1, y: 0, duration: 0.7, stagger: 0.1 }, at);
    playDashboard(tl, q, at - 0.3);
  });

  return (
    <Section ref={ref} id="gateway" tone="card">
      <div
        className={cn(
          shellClass,
          'grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:gap-16',
        )}
      >
        <div className="grid gap-10">
          <SectionHeader
            size="column"
            eyebrow={gateway.eyebrow}
            title={gateway.title}
            lede={gateway.lede}
          />
          <ul className="m-0 grid list-none p-0">
            {gateway.features.map((feature) => {
              const FeatureIcon = ICONS[feature.id];
              return (
                <li key={feature.id}>
                  <Reveal
                    data-feature
                    className="grid grid-cols-[2.5rem_1fr] gap-4 border-t border-border py-5"
                  >
                    <span
                      aria-hidden
                      className="grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-brand-sky"
                    >
                      <FeatureIcon size={20} weight="duotone" />
                    </span>
                    <div className="grid gap-1">
                      <h3 className="m-0 text-base font-semibold">{feature.title}</h3>
                      <p className="m-0 text-sm leading-relaxed text-muted-foreground">
                        {feature.body}
                      </p>
                      <p className="m-0 mt-1 text-sm font-medium text-brand-sky">
                        {feature.outcome}
                      </p>
                    </div>
                  </Reveal>
                </li>
              );
            })}
          </ul>
        </div>

        <DashboardMock />
      </div>
    </Section>
  );
}
